import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, writeBatch, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { Button } from '../components/UI';

export default function AccessSharedDataScreen({ user, navigate }) {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!code || code.length !== 6) {
            setError('Kode harus terdiri dari 6 digit angka.');
            return;
        }

        if (!user?.uid) {
            setError('Anda harus login terlebih dahulu.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const q = query(
                collection(db, 'shareCodes'), 
                where("code", "==", code),
                where("active", "==", true)
            );
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                setError('Kode berbagi tidak valid atau tidak ditemukan.');
                return;
            }

            const shareDoc = querySnapshot.docs[0];
            const shareData = shareDoc.data();

            if (shareData.expiresAt && shareData.expiresAt.toDate() < new Date()) {
                setError('Kode berbagi sudah kedaluwarsa. Minta kode baru dari orang tua.');
                return;
            }
            
            if (shareData.claimedBy && shareData.claimedBy !== user.uid) {
                setError('Kode ini sudah digunakan oleh tenaga kesehatan lain.');
                return;
            }

            // Periksa apakah dokumen anak ada di koleksi root
            const childRef = doc(db, 'children', shareData.childId);
            const childDoc = await getDoc(childRef);
            
            if (!childDoc.exists()) {
                setError('Data anak tidak ditemukan di sistem.');
                return;
            }

            const childData = childDoc.data();
            const roleField = user.role === 'Tenaga Kesehatan' ? 'nakes' : user.role === 'Guru PAUD' ? 'teachers' : 'parents';

            if (childData[roleField] && childData[roleField].includes(user.uid)) {
                setSuccess(`Anda sudah memiliki akses untuk anak bernama ${shareData.childName}.`);
                setCode('');
                setTimeout(() => navigate('home'), 2000);
                return;
            }

            const batch = writeBatch(db);
            
            // Tambahkan UID ke array akses anak
            batch.update(childRef, {
                [roleField]: arrayUnion(user.uid)
            });

            batch.update(shareDoc.ref, { 
                claimedBy: user.uid,
                claimedAt: new Date()
            });

            await batch.commit();

            setSuccess(`Akses untuk ${shareData.childName} berhasil! Anak akan muncul di beranda.`);
            setCode('');
            setTimeout(() => {
                navigate('home');
            }, 3000);

        } catch (err) {
            console.error("Error accessing shared data:", err);
            setError(`Terjadi kesalahan: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-indigo-200 pb-28 pt-8 px-5 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[10%] left-[-20%] w-[60vw] h-[60vw] bg-sky-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute top-[40%] right-[-20%] w-[50vw] h-[50vw] bg-indigo-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[10%] w-[70vw] h-[70vw] bg-blue-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-4000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 max-w-md mx-auto">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-white/60 backdrop-blur-md rounded-[1.25rem] flex items-center justify-center text-3xl mx-auto mb-4 shadow-sm border border-white">
                        🔐
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">Akses Data Anak</h2>
                    <p className="text-sm text-gray-500 mt-2">Masukkan 6 digit kode dari orang tua pasien.</p>
                </div>

                <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-6 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] relative overflow-hidden">
                    <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                        {error && (
                            <div className="bg-rose-50 border border-rose-200 text-rose-600 p-3.5 rounded-xl text-sm font-medium flex items-start space-x-2">
                                <span>⚠️</span>
                                <span>{error}</span>
                            </div>
                        )}
                        
                        {success && (
                            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 p-3.5 rounded-xl text-sm font-medium flex items-start space-x-2">
                                <span>✅</span>
                                <span>{success}</span>
                            </div>
                        )}
                        
                        <div>
                            <label className="block text-gray-700 text-xs font-bold mb-2 uppercase tracking-wide text-center">Kode Akses Khusus</label>
                            <input 
                                type="text" 
                                value={code} 
                                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                                placeholder="000000"
                                maxLength={6}
                                required 
                                className="w-full bg-white/80 border border-gray-200 text-gray-800 text-3xl text-center tracking-[0.3em] font-extrabold rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block py-5 shadow-sm outline-none transition-all placeholder-gray-300"
                            />
                        </div>
                        
                        <Button 
                            type="submit" 
                            disabled={loading || code.length !== 6}
                            className="w-full bg-gray-900 text-white hover:bg-gray-800 shadow-xl py-4 rounded-xl mt-4 transition-all"
                        >
                            {loading ? 'Memverifikasi Kode...' : 'Dapatkan Akses'}
                        </Button>
                    </form>
                </div>
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
            `}</style>
        </div>
    );
}
